import React, { useEffect, useRef } from 'react';
import { Button, message } from 'antd';
import UploadModal from './UploadModal';
import useDeleteLicense from 'hooks/useDeleteLicense';
import { ELicenseType } from 'types/common';
import s from './s.module.less';
import { ILicenseData } from 'types/newUniprofile';
import { providerLicenseUpdate } from 'api/provider';
import { getUserId } from 'utils/localstore';
import LicenseImg from 'components/LicenseImg';
import { isEqual } from 'lodash';

interface AdditionalLicensesNCertificatesType {
    autonomy?: ILicenseData;
    other?: ILicenseData;
}
interface Props {
    initValue?: AdditionalLicensesNCertificatesType;
    isEditing?: boolean;
    refetch?: () => void;
}

const AdditionalLicensesNCertificates = (props: Props) => {
    const { isEditing, initValue, refetch } = props;
    const [submiting, setSubmiting] = React.useState(false);
    const [modalTitle, setModalTitle] = React.useState('');
    const [showModal, setShowModal] = React.useState(false);
    const [currentUploading, setCurrentUploading] = React.useState<ELicenseType>();
    const [currentRemoveId, setCurrentRemoveId] = React.useState<number>();
    const [autonomyValue, setAutonomyValue] = React.useState<ILicenseData>();
    const [otherValues, setOtherValues] = React.useState<ILicenseData>();
    const prevInitValueRef = useRef<AdditionalLicensesNCertificatesType>();
    const [, deleteLicense, removeLoading] = useDeleteLicense({});

    const handleShowModal = (v: ELicenseType) => {
        setCurrentUploading(v);
        setModalTitle(v === ELicenseType.AUTONOMY ? 'Add certificate of autonomy' : 'Add other certificates');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    useEffect(() => {
        if (initValue && !isEqual(prevInitValueRef.current, initValue)) {
            prevInitValueRef.current = initValue;
            setAutonomyValue(initValue.autonomy);
            setOtherValues(initValue.other);
        }
    }, [initValue]);

    const onSubmit = async (value: string) => {
        const data = currentUploading === ELicenseType.AUTONOMY ? autonomyValue : otherValues;
        try {
            const res = await providerLicenseUpdate({
                type: currentUploading,
                url: value,
                id: data?.id,
            }, data?.providerId || getUserId()!);
            if (res.error) {
                message.error(res.error);
            } else {
                handleCloseModal();
                refetch?.();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleOk = (v: string) => {
        onSubmit(v);
    };

    const renderEditing = () => {
        return (
            <div className={s.editWrap}>
                <div className={s.editItem}>
                    <div className={s.editHeader}>
                        <div className={s.addIcon} onClick={() => handleShowModal(ELicenseType.AUTONOMY)} />
                        <div className={s.title}>Add certificate of autonomy</div>
                    </div>
                    {
                        autonomyValue?.url && (
                            <div className={s.itemList}>
                                <div className={s.item}>
                                    <div className={s.text}>{autonomyValue?.url}</div>
                                    <Button
                                        className={s.removeButton}
                                        loading={currentRemoveId === autonomyValue?.id && removeLoading}
                                        ghost
                                        onClick={() => {
                                            setCurrentRemoveId(autonomyValue?.id);
                                            deleteLicense([autonomyValue?.id!], () => refetch?.());
                                        }}
                                    >remove
                                    </Button>
                                </div>
                            </div>
                        )
                    }
                </div>
                <div className={s.editItem}>
                    <div className={s.editHeader}>
                        <div className={s.addIcon} onClick={() => handleShowModal(ELicenseType.OTHER)} />
                        <div className={s.title}>Other certificates</div>
                    </div>
                    {
                        otherValues?.url && (
                            <div className={s.itemList}>
                                <div className={s.item}>
                                    <div className={s.text}>{otherValues?.url}</div>
                                    <Button
                                        className={s.removeButton}
                                        loading={currentRemoveId === otherValues?.id && removeLoading}
                                        type="ghost"
                                        onClick={async () => {
                                            setCurrentRemoveId(otherValues?.id);
                                            await deleteLicense([otherValues?.id!], () => refetch?.());
                                        }}
                                    >remove
                                    </Button>
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
        );
    };

    const renderReview = () => {
        return (
            (
                <div className="mt-6">
                    <div className={s.displayItem}>
                        <div className={s.label}>Certificate of autonomy</div>
                        {autonomyValue?.url ?
                            <LicenseImg src={autonomyValue?.url} />
                            :
                            <div className="mt-2">
                                Not provided
                            </div>}
                    </div>
                    <div className={s.displayItem}>
                        <div className={s.label}>Other certificates</div>
                        {otherValues?.url ?
                            <LicenseImg src={otherValues?.fullUrl || otherValues?.url} />
                            :
                            <div className="mt-2">
                                Not provided
                            </div>}
                    </div>
                </div>

            )
        );
    };

    return (
        <div>
            {isEditing ? renderEditing() : renderReview()}
            <UploadModal
                title={modalTitle}
                submiting={submiting}
                onCancel={handleCloseModal}
                open={showModal}
                onOk={handleOk}
                value=""
            />
        </div>
    );
};

export default AdditionalLicensesNCertificates;
